import { GenericAbortSignal } from 'axios';

import type { AIPrompt as AIPromptType } from 'models/flexible/AIPrompt';
import { AIPrompt, AIPromptOptions } from 'models/flexible/AIPrompt';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminAIPromptCreateParams,
  AdminAIPromptIndexFields,
  AdminAIPromptIndexInclude,
  AdminAIPromptShowFields,
  AdminAIPromptShowInclude,
  AdminAIPromptUpdateParams,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminAIPromptsQueryKeys = {
  index: (params?: Record<string, any>) => ['adminAIPrompts', params] as const,
  show: (id: string) => ['adminAIPrompt', id] as const,
};

const URL = 'admin/ai_prompts';

export type AdminAIPromptIndexParams = BaseIndexQueryParams & {
  targetClassname?: string;
  'target_classname.not'?: string;
  client?: string;
  groupType?: string;
  planTypeClassifications?: string;
  episodeClassifications?: string;
  type?: string;
  isPublished?: string;
  include?: string;
};

export async function adminIndexAIPrompts<I extends AdminAIPromptIndexInclude = never>(
  params: AdminAIPromptIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<AIPromptType<AdminAIPromptIndexFields<I>>>> {
  return http
    .get<
      Paginated<AIPromptOptions>
    >(URL, { params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) }, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new AIPrompt(x)),
    })) as unknown as Promise<Paginated<AIPromptType<AdminAIPromptIndexFields<I>>>>;
}

export async function adminShowAIPrompt<I extends AdminAIPromptShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: readonly I[]
): Promise<AIPromptType<AdminAIPromptShowFields<I>>> {
  return http
    .get<AIPromptOptions>(`${URL}/${id}`, { params: { include: includes?.join(',') }, signal })
    .then((res) => new AIPrompt(res.data)) as unknown as Promise<AIPromptType<AdminAIPromptShowFields<I>>>;
}

export async function createAIPrompt(params: AdminAIPromptCreateParams) {
  return http.post<AIPromptOptions>(URL, params).then((res) => new AIPrompt(res.data));
}

export async function updateAIPrompt(id: string, params: AdminAIPromptUpdateParams) {
  return http.patch<AIPromptOptions>(`${URL}/${id}`, params).then((res) => new AIPrompt(res.data));
}

export async function deleteAIPrompt(id: string) {
  return http.delete(`${URL}/${id}`);
}

// ---------------------------------------------------------------------------
// Infinite scroll hooks (moved from services/api/aiPrompts.ts)
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

type AdminInfiniteParams = {
  type?: string;
  'prompts.distinct'?: boolean;
} & BaseIndexQueryParams;

async function indexAdminAiPrompts(params: AdminInfiniteParams, signal?: GenericAbortSignal) {
  return http.get<Paginated<AIPromptOptions>>(URL, { params, signal }).then((response) => ({
    ...response.data,
    data: response.data.data.map((x) => new AIPrompt(x)),
  }));
}

export const useInfiniteAdminAiPrompts = (params: AdminInfiniteParams) => {
  const [search, setSearch] = useState<string>();

  const options = useInfiniteQuery({
    queryKey: adminAIPromptsQueryKeys.index({ infinite: true, ...params, search }),
    queryFn: ({ signal, pageParam }) =>
      indexAdminAiPrompts({ pageSize: 10, ...params, search, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: false,
  });

  useEffect(() => {
    if (search !== undefined) options.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { options, onDebouncedSearch: setSearch };
};
